import type { TasksFromTextResult } from '@fos/shared/contracts/copilot';
import { Sparkles } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { trpc } from '../../../lib/trpc';
import { ContextBanner, FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Textarea } from '../../../ui/Input';
import { copilotErrorInfo } from '../copilot-ui';
import { useCopilotInvalidate } from '../hooks';
import { TaskProposalsBlock } from './TaskProposalsBlock';

/**
 * „Zadania z tekstu” (MVP §6, §10, Etap 6): wklejony tekst albo wiadomość → AI proponuje zadania → osoba wybiera, które
 * dodać (propozycje do przeglądu, nic nie powstaje samo). Polecenia dla AI w treści maila są tylko danymi (ostrzeżenie
 * nad listą). Transkrypcje spotkań przetwarza widok spotkania („Przetwórz”). Wyłączone AI = komunikat, zadania dodasz
 * ręcznie jak zawsze.
 */
export function TasksFromTextDialog({
  open,
  onOpenChange,
  messageId,
  clientId,
  projectId,
  initialText = '',
  sourceLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** wiadomość jako źródło (bez pola tekstu) */
  messageId?: string;
  clientId?: string | null;
  projectId?: string | null;
  initialText?: string;
  /** „wiadomość od Anny Kowalczyk” */
  sourceLabel?: string;
}) {
  const [text, setText] = useState(initialText);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [result, setResult] = useState<TasksFromTextResult | null>(null);
  const run = trpc.copilot.tasksFromText.useMutation();
  const invalidate = useCopilotInvalidate();

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset przy każdym otwarciu
  useEffect(() => {
    if (!open) return;
    setText(initialText);
    setResult(null);
    setFieldError(null);
    run.reset();
  }, [open]);

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!messageId && !text.trim()) {
      setFieldError('Wklej tekst, z którego AI ma wypisać zadania.');
      return;
    }
    setFieldError(null);
    try {
      const res = await run.mutateAsync(
        messageId
          ? { messageId }
          : {
              text: text.trim(),
              ...(clientId ? { clientId } : {}),
              ...(projectId ? { projectId } : {}),
            },
      );
      setResult(res);
      void invalidate.jobs();
    } catch {
      // błąd pokazujemy pod formularzem
    }
  };

  const error = run.isError ? copilotErrorInfo(run.error) : null;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title="Zadania z tekstu"
      description={
        messageId
          ? 'AI wypisze zadania z wiadomości. Ty decydujesz, które dodać.'
          : 'Wklej notatkę albo maila. AI wypisze zadania, a Ty zdecydujesz, które dodać.'
      }
      footer={
        result ? (
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Zamknij
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Anuluj
            </Button>
            <Button variant="primary" icon={Sparkles} type="submit" form="copilot-tasks-form" loading={run.isPending}>
              Zaproponuj zadania
            </Button>
          </>
        )
      }
    >
      {!result && (
        <form id="copilot-tasks-form" onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
          {!messageId && (
            <Field label="Tekst" error={fieldError}>
              <Textarea
                autoFocus
                rows={6}
                maxLength={20_000}
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  setFieldError(null);
                }}
                placeholder="np. notatka z rozmowy: „dodać sekcję FAQ, poprawić stopkę, wysłać wycenę do piątku”"
              />
            </Field>
          )}
          {messageId && sourceLabel && <p className="text-body-s text-fg-secondary">Źródło: {sourceLabel}</p>}
          {run.isPending && (
            <p role="status" className="text-body-s text-fg-muted">
              AI pracuje…
            </p>
          )}
          {error && <FormMessage>{error.message}</FormMessage>}
        </form>
      )}
      {result && (
        <div className="flex flex-col gap-3">
          {result.warnings.map((w) => (
            <ContextBanner key={w} tone="warning">
              {w}
            </ContextBanner>
          ))}
          {result.existing > 0 && result.created === 0 && (
            <p className="text-body-s text-fg-secondary">Te propozycje już były — nic nie zostało zdublowane.</p>
          )}
          <TaskProposalsBlock
            sourceType={result.sourceType === 'message' ? 'message' : 'ai'}
            sourceId={result.sourceId}
            initial={result.proposals}
            meta={result.meta}
            source={sourceLabel ? `Źródło: ${sourceLabel}` : messageId ? 'Źródło: wiadomość' : 'Źródło: wklejony tekst'}
          />
        </div>
      )}
    </Dialog>
  );
}
